import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Hook to prevent browser back/forward navigation.
 * Silently blocks navigation without showing any confirmation.
 * Use for critical processes like file uploads or processing that cannot be interrupted.
 *
 * @param shouldPrevent - Whether navigation should be prevented
 *
 * @example
 * ```tsx
 * // In a processing modal:
 * usePreventNavigation(isProcessing);
 * ```
 */
export const usePreventNavigation = (shouldPrevent: boolean) => {
  const location = useLocation();
  const currentPathRef = useRef(location.pathname + location.search);
  const hasGuardRef = useRef(false);

  useEffect(() => {
    currentPathRef.current = location.pathname + location.search;
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!shouldPrevent) {
      // Remove guard if it exists
      if (hasGuardRef.current) {
        window.history.back();
        hasGuardRef.current = false;
      }
      return;
    }

    // Add guard entry
    if (!hasGuardRef.current) {
      window.history.pushState({ __preventNav__: true }, "", currentPathRef.current);
      hasGuardRef.current = true;
    }

    const handlePopState = () => {
      // Silently restore position - no modal, just block
      window.history.pushState({ __preventNav__: true }, "", currentPathRef.current);
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldPrevent]);
};

export default usePreventNavigation;
